import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarHeader,
  SidebarFooter,
} from "../../components/ui/sidebar";

import {
  Calculator,
  CalendarCheck,
  CircleUserRound,
  // FilePlusCorner, // usado só pelo item "Gerar Boleto", hoje oculto
  FileText,
  FileUp,
  LayoutDashboard,
  LogOut,
  MessageSquareText,
  Package,
  Settings,
  SquarePen,
  Truck,
  TriangleAlert,
  Wallet,
} from "lucide-react";

import Logo from "../Logo";
import NavLink from "../NavLink";
import { useContext, useEffect, useState } from "react";
import ModalCaixas from "components/CustomModal/ModalContent/ModalCaixas";
import ModalAgendarColeta from "components/CustomModal/ModalContent/ModalAgendarColeta";
// import ModalBoleto from "components/CustomModal/ModalContent/ModalBoleto";
import ModalImportarPedidos from "components/CustomModal/ModalContent/ModalImportarPedidos";
import ModalCotacao from "components/CustomModal/ModalContent/ModalCotacao";
import { useTranslation } from "react-i18next";
import ModalSettings from "components/CustomModal/ModalContent/ModalSettings";
import { AuthContext } from "context/AuthContext";
import ModalRealizarCotacaoNoCsv from "components/CustomModal/ModalContent/ModalRealizarCotacaoNoCsv";
// import ModalWorldEase from "components/CustomModal/ModalContent/ModalWorldEase";
import ModalColetasAgendadas from "components/CustomModal/ModalContent/ModalColetasAgendadas";
import ModalSimulacao from "components/CustomModal/ModalContent/ModalSimulacao";
import ModalOcorrencias from "components/CustomModal/ModalContent/ModalOcorrencias";
import { useNavigate } from "react-router-dom";
import { getCotacoes } from "services/cotacoes";
import ModalCarteira from "components/CustomModal/ModalContent/ModalCarteira";
import { aoAbrirCarteira, getCarteira } from "services/carteira/carteira.service";

/** Rótulo dos grupos: menor, em caixa alta e apagado, para não competir com os itens. */
const GRUPO =
  "mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-brand-cardtext/40";

const AppSidebar = () => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();

  // O item aceso é o modal aberto. Antes existia um activeTab paralelo que
  // nunca era atualizado, então o Dashboard ficava destacado para sempre e
  // nenhum outro item acendia.
  const [modalIndex, setModalIndex] = useState(-1);
  const auth = useContext(AuthContext);

  // Contador do selo vermelho. limit:1 porque só interessa o total da
  // resposta, não as linhas. Falha em silêncio: sem selo é melhor que
  // derrubar o menu inteiro.
  const [ocorrencias, setOcorrencias] = useState(0);

  // Carteira só aparece para contas liberadas (allowlist no servidor).
  const [carteiraLiberada, setCarteiraLiberada] = useState(false);

  useEffect(() => {
    getCarteira()
      .then((r) => setCarteiraLiberada(r.habilitada === true))
      .catch(() => setCarteiraLiberada(false));
  }, []);

  // O saldo no cabeçalho pede para abrir a carteira.
  useEffect(() => aoAbrirCarteira(() => setModalIndex(14)), []);

  useEffect(() => {
    // Mesmo corte de 90 dias da tela de Ocorrências: senão o selo contaria
    // exceções antigas que já não têm como se resolver.
    const corte = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);

    getCotacoes({ status: "EXCECAO", start_day: corte, limit: 1 })
      .then((res) => setOcorrencias(Number(res?.total) || 0))
      .catch(() => setOcorrencias(0));
  }, [modalIndex]);

  return (
    <Sidebar
      variant="inset"
      className="bg-brand-sidebar text-brand-cardtext max-w-[240px]"
    >
      <SidebarHeader>
        <Logo />
      </SidebarHeader>

      <SidebarContent>
        {/* Dashboard fica solto no topo: é a tela de fundo, não pertence a
            nenhum dos grupos de ação. */}
        <SidebarGroup className="pb-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              <NavLink
                icon={<LayoutDashboard />}
                name={t("navBar.dashboard")}
                active={modalIndex === -1}
                action={() => setModalIndex(-1)}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="py-1">
          <SidebarGroupLabel className={GRUPO}>Operação</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              <NavLink
                icon={<FileText />}
                name={t("navBar.quotes")}
                active={modalIndex === 1}
                action={() => setModalIndex(1)}
              />
              <NavLink
                icon={<TriangleAlert />}
                name="Ocorrências"
                badge={ocorrencias}
                active={modalIndex === 13}
                action={() => setModalIndex(13)}
              />
              <NavLink
                icon={<Calculator />}
                name="Simulador de Frete"
                active={modalIndex === 12}
                action={() => setModalIndex(12)}
              />
              <NavLink
                icon={<SquarePen />}
                name={t("navBar.manual_quote")}
                active={modalIndex === 6}
                action={() => setModalIndex(6)}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {carteiraLiberada && (
          <SidebarGroup className="py-1">
            <SidebarGroupLabel className={GRUPO}>Financeiro</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                <NavLink
                  icon={<Wallet />}
                  name="Carteira Intrex"
                  active={modalIndex === 14}
                  action={() => setModalIndex(14)}
                />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarGroup className="py-1">
          <SidebarGroupLabel className={GRUPO}>Cadastros</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              <NavLink
                icon={<Package />}
                name={t("navBar.boxes")}
                active={modalIndex === 2}
                action={() => setModalIndex(2)}
              />
              <NavLink
                icon={<FileUp />}
                name={t("navBar.import_orders")}
                active={modalIndex === 4}
                action={() => setModalIndex(4)}
              />
              {/* Gerar Boleto: oculto do menu a pedido. Modal e rota seguem
                  no código, é só descomentar aqui e o render lá embaixo.
              <NavLink
                icon={<FilePlusCorner />}
                name={t("navBar.generate_invoice")}
                active={modalIndex === 3}
                action={() => setModalIndex(3)}
              />
              */}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="py-1">
          <SidebarGroupLabel className={GRUPO}>Coletas</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              {/* Coleta FedEx e Coleta UPS viraram um item só: a transportadora
                  é escolhida dentro do modal. */}
              <NavLink
                icon={<Truck />}
                name={t("navBar.schedule_pickup")}
                active={modalIndex === 5}
                action={() => setModalIndex(5)}
              />
              <NavLink
                icon={<CalendarCheck />}
                name={t("navBar.scheduled_pickups")}
                active={modalIndex === 11}
                action={() => setModalIndex(11)}
              />
              {/* WorldEase UPS: oculto do menu a pedido. Modal preservado.
              <NavLink
                icon={<Package />}
                name="WorldEase UPS"
                active={modalIndex === 10}
                action={() => setModalIndex(10)}
              />
              */}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="mb-4">
        <SidebarMenu className="gap-1">
          <NavLink
            icon={<Settings />}
            name={t("navBar.settings")}
            active={modalIndex === 7}
            action={() => setModalIndex(7)}
          />

          <NavLink
            icon={<CircleUserRound />}
            name={auth?.user?.razaoSocial || "Usuário"}
            action={() => setModalIndex(8)}
          />

          <a
            href="https://wa.me/554792104226"
            target="_blank"
            rel="noopener noreferrer"
          >
            <NavLink
              icon={<MessageSquareText />}
              name={t("navBar.talk_to_us")}
              action={() => setModalIndex(8)}
            />
          </a>
          <NavLink
            icon={<LogOut />}
            name={"Logout"}
            action={async () => {
              await auth?.logout();
              navigate("/login", { replace: true });
            }}
          />
        </SidebarMenu>
      </SidebarFooter>

      {/* MODAIS (fora da estrutura visual) */}
      <ModalCotacao
        logo={<FileText />}
        setShowNumber={setModalIndex}
        show={modalIndex === 1}
        title={t("navBar.quotes")}
      />

      <ModalOcorrencias
        logo={<TriangleAlert />}
        setShowNumber={setModalIndex}
        show={modalIndex === 13}
        title="Ocorrências"
      />

      <ModalCarteira
        logo={<Wallet />}
        setShowNumber={setModalIndex}
        show={modalIndex === 14}
        title="Carteira Intrex"
      />

      <ModalSimulacao
        logo={<Calculator />}
        setShowNumber={setModalIndex}
        show={modalIndex === 12}
        title="Simulador de Frete"
      />

      <ModalCaixas
        logo={<Package />}
        setShowNumber={setModalIndex}
        show={modalIndex === 2}
        title={t("navBar.boxes")}
      />

      {/* Gerar Boleto: item oculto do menu, modal preservado.
      <ModalBoleto
        logo={<FilePlusCorner />}
        setShowNumber={setModalIndex}
        show={modalIndex === 3}
        title={t("navBar.generate_invoice")}
      />
      */}

      <ModalImportarPedidos
        logo={<FileUp />}
        setShowNumber={setModalIndex}
        show={modalIndex === 4}
        title={t("navBar.import_orders")}
      />

      {/* Sem logo: o selo redondo flutuante nao identificava nada que o
          titulo ja nao dissesse, e quebrava o retangulo do modal. */}
      <ModalAgendarColeta
        setShowNumber={setModalIndex}
        show={modalIndex === 5}
        title={t("navBar.schedule_pickup")}
      />

      <ModalRealizarCotacaoNoCsv
        logo={<FileText />}
        setShowNumber={setModalIndex}
        show={modalIndex === 6}
        title={t("navBar.manual_quote")}
      />

      {/* WorldEase UPS: item oculto do menu, modal preservado.
      <ModalWorldEase
        logo={<Package />}
        setShowNumber={setModalIndex}
        show={modalIndex === 10}
        title="WorldEase UPS"
      />
      */}

      <ModalColetasAgendadas
        logo={<CalendarCheck />}
        setShowNumber={setModalIndex}
        show={modalIndex === 11}
        title={t("navBar.scheduled_pickups")}
      />

      <ModalSettings
        logo={<Settings />}
        setShowNumber={setModalIndex}
        show={modalIndex === 7}
        title={t("navBar.settings")}
      />
    </Sidebar>
  );
};

export default AppSidebar;